<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">

    <div class="bg-white dark:bg-slate-900 w-full max-w-3xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ form.id ? 'Editar Usuario' : 'Nuevo Usuario' }}
          </h2>
          <p class="text-xs text-slate-500">Completa la información profesional del colaborador.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="p-6 overflow-y-auto custom-scrollbar space-y-8">

        <!-- CABECERA: FOTO Y ESTADO -->
        <div class="flex items-center gap-6">
          <div class="relative group">
            <img
              :src="form.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(form.full_name || 'Nuevo Usuario')}&background=f8fafc&color=475569&bold=true&size=150`"
              class="w-24 h-24 rounded-full object-cover border-4 border-slate-100 dark:border-slate-800 shadow-sm"
              :class="{'opacity-50': isUploadingPhoto}"
            />
            <label class="absolute bottom-0 right-0 bg-brand-600 text-white p-2 rounded-full cursor-pointer hover:bg-brand-700 shadow-lg transition-transform hover:scale-110">
              <Loader2 v-if="isUploadingPhoto" class="w-4 h-4 animate-spin" />
              <Camera v-else class="w-4 h-4" />
              <input type="file" class="hidden" accept="image/*" @change="handlePhotoUpload">
            </label>
          </div>
          <div>
            <label class="flex items-center gap-3 cursor-pointer select-none">
              <div class="relative">
                <input type="checkbox" v-model="form.is_active" class="sr-only peer">
                <div class="w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </div>
              <div>
                <span class="block text-sm font-bold" :class="form.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'">
                  {{ form.is_active ? 'Cuenta Activa' : 'Cuenta Suspendida' }}
                </span>
                <span class="block text-xs text-slate-400">
                  {{ form.is_active ? 'El usuario tiene acceso al sistema.' : 'Acceso revocado temporalmente.' }}
                </span>
              </div>
            </label>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" id="userForm" class="grid grid-cols-1 md:grid-cols-2 gap-5">

          <div class="md:col-span-2">
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Nombre Completo</label>
            <div class="relative">
              <User class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="form.full_name" type="text" required :class="[inputClass, 'pl-10']" placeholder="Ej: Juan Antonio Pérez" />
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Correo Electrónico (Login)</label>
            <div class="relative">
              <Mail class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="form.email" type="email" required :class="[inputClass, 'pl-10']" placeholder="juan@empresa.com" />
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Teléfono Móvil</label>
            <div class="relative">
              <Phone class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="form.phone" type="tel" :class="[inputClass, 'pl-10']" placeholder="+505 0000 0000" />
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Documento Identidad</label>
            <div class="relative">
              <FileText class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="form.document" type="text" :class="[inputClass, 'pl-10']" placeholder="Cédula / Pasaporte" />
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Cargo / Puesto de Trabajo</label>
            <div class="relative">
              <Briefcase class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="form.job_title" type="text" :class="[inputClass, 'pl-10']" placeholder="Ej: Asesor de Crédito Sr." />
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Rol de Sistema (Permisos)</label>
            <div class="relative">
              <Shield class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select v-model="form.role_id" required :class="[inputClass, 'pl-10', 'appearance-none']">
                <option value="" disabled>Seleccionar Rol</option>
                <option v-for="r in rolesList" :key="r.id" :value="r.id">{{ r.name }}</option>
              </select>
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Sucursal Asignada</label>
            <div class="relative">
              <Building class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select v-model="form.fk_branch_id" :class="[inputClass, 'pl-10', 'appearance-none']">
                <option value="" disabled>Seleccionar Sucursal</option>
                <option v-for="b in branchesList" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
          </div>

          <div class="md:col-span-2">
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Dirección de Domicilio</label>
            <div class="relative">
              <MapPin class="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <textarea v-model="form.address" rows="2" :class="[inputClass, 'pl-10', 'resize-none']" placeholder="Dirección residencial del colaborador..."></textarea>
            </div>
          </div>

          <!-- SEGURIDAD MEJORADA -->
          <div class="md:col-span-2 border-t border-slate-100 dark:border-slate-800 pt-5 mt-2">
            <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Lock class="w-4 h-4 text-brand-600" /> Seguridad de Acceso
            </h3>

            <!-- ESCENARIO 1: CREANDO USUARIO NUEVO -->
            <div v-if="!form.id">
              <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Contraseña Temporal Inicial</label>
              <div class="relative">
                <Key class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  v-model="form.password"
                  type="password"
                  required
                  :class="[inputClass, 'pl-10']"
                  placeholder="Asignar contraseña temporal"
                />
              </div>
              <p class="text-[10px] text-slate-400 mt-1">Deberás notificarle esta contraseña al usuario. Se le pedirá cambiarla al entrar.</p>
            </div>

            <!-- ESCENARIO 2: EDITANDO USUARIO EXISTENTE -->
            <div v-else class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <div>
                <p class="text-sm font-bold text-slate-700 dark:text-slate-300">Restablecer Contraseña</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">Enviaremos un correo electrónico con un enlace seguro para que el usuario asigne una nueva clave.</p>
              </div>
              <button
                type="button"
                @click="handlePasswordReset"
                :disabled="isResetting"
                class="shrink-0 px-4 py-2 text-xs font-bold text-brand-700 bg-brand-100 hover:bg-brand-200 dark:bg-brand-900/30 dark:text-brand-400 dark:hover:bg-brand-900/50 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Loader2 v-if="isResetting" class="w-4 h-4 animate-spin" />
                <Mail v-else class="w-4 h-4" />
                {{ isResetting ? 'Enviando...' : 'Enviar Enlace' }}
              </button>
            </div>
          </div>

        </form>
      </div>

      <!-- FOOTER -->
      <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
        <button
          @click="$emit('close')"
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="userForm"
          :disabled="isSaving || isUploadingPhoto"
          class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center gap-2 disabled:opacity-70"
        >
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          {{ form.id ? 'Guardar Cambios' : 'Crear Usuario' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import {
  X, Save, Loader2, User, Mail, Lock, FileText, Building,
  Shield, Camera, Phone, Briefcase, MapPin, Key
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useUpload } from '@/composables/useUpload';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  show: Boolean,
  userToEdit: Object
});

const emit = defineEmits(['close', 'saved']);

const inputClass = "w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all dark:text-white";

// Composables
const { uploading: isUploadingPhoto, uploadFile } = useUpload();
const { showSuccess, showError, showWarning } = useToast();

const rolesList = ref([]);
const branchesList = ref([]);
const isLoadingDeps = ref(false);
const isSaving = ref(false);
const isResetting = ref(false); // NUEVO ESTADO PARA EL BOTÓN

const currentUser = SecureStorage.getItem('llevo_user');
const currentCompanyId = currentUser?.company;

const form = reactive({
  id: null,
  full_name: '',
  username: '',
  email: '',
  phone: '',
  job_title: '',
  address: '',
  document: '',
  password: '',
  role_id: '',
  fk_branch_id: '',
  is_active: true,
  photo_url: '',
  company: currentCompanyId
});

watch(() => props.userToEdit, (newUser) => {
  if (newUser) {
    Object.assign(form, {
      id: newUser.id,
      full_name: newUser.full_name,
      username: newUser.username,
      email: newUser.email,
      phone: newUser.phone || '',
      job_title: newUser.job_title || '',
      address: newUser.address || '',
      document: newUser.document,
      role_id: newUser.role_id,
      fk_branch_id: newUser.fk_branch_id,
      is_active: Boolean(newUser.is_active),
      photo_url: newUser.photo_url,
      company: newUser.company,
      password: '' // Ya no se usa en el UI, pero lo limpiamos por seguridad
    });
  } else {
    Object.assign(form, {
      id: null,
      full_name: '',
      username: '',
      email: '',
      phone: '',
      job_title: '',
      address: '',
      document: '',
      password: '',
      role_id: '',
      fk_branch_id: '',
      is_active: true,
      photo_url: '',
      company: currentCompanyId
    });
  }
}, { immediate: true });

onMounted(async () => {
  if (!currentCompanyId) return;
  isLoadingDeps.value = true;
  try {
    const [resRoles, resBranches] = await Promise.all([
      apiClient.get(`/roles`),
      apiClient.get(`/branches?company=${currentCompanyId}`)
    ]);

    rolesList.value = Array.isArray(resRoles.data) ? resRoles.data : (resRoles.data.data || []);
    branchesList.value = Array.isArray(resBranches.data) ? resBranches.data : (resBranches.data.data || []);

  } catch (error) {
    console.error("Error cargando dependencias", error);
  } finally {
    isLoadingDeps.value = false;
  }
});

const handlePhotoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const url = await uploadFile(file, "user_avatars");
  if (url) form.photo_url = url;
  event.target.value = '';
};

// NUEVA FUNCIÓN DE RESTABLECIMIENTO DE CONTRASEÑA
const handlePasswordReset = async () => {
  if (!form.email) return;

  isResetting.value = true;
  try {
    // Asegúrate de tener este endpoint en tu PHP (ej: envía un correo con un token o genera una nueva)
    await apiClient.post('/password/request', { email: form.email });
    showSuccess(`Se ha enviado un enlace de recuperación a ${form.email}`);
  } catch (error) {
    console.error(error);
    showError(error.response?.data?.message || 'Error al solicitar el restablecimiento.');
  } finally {
    isResetting.value = false;
  }
};

const handleSubmit = async () => {
  if (!form.role_id || !form.fk_branch_id) {
    showWarning('Debes seleccionar un Rol y una Sucursal.');
    return;
  }

  isSaving.value = true;
  try {
    const payload = { ...form };

    // Si estamos editando, eliminamos el password del payload para no sobreescribir nada accidentalmente
    if (form.id) {
      delete payload.password;
    }

    if (!payload.username) payload.username = payload.email;

    if (form.id) {
      await apiClient.put(`/users/${form.id}`, payload);
      showSuccess('Usuario modificado correctamente.');
    } else {
      await apiClient.post('/users', payload);
      showSuccess('Usuario registrado correctamente.');
    }

    emit('saved');
    emit('close');

  } catch (error) {
    console.error(error);
    const msg = error.response?.data?.error || 'No se pudo guardar el usuario.';
    showError(msg);
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}
</style>